import { useMemo, useState } from "react";

import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";

import { useMutation, useQuery } from "@tanstack/react-query";
import { BuildingIcon } from "lucide-react";

import { formatDate } from "simplydo/core/dates";
import { asMutation, initUseQuery } from "simplydo/core/query";
import type { OpenAPI } from "simplydo/interfaces";
import { Button } from "simplydo/react/ui/button";
import { H4, Subheader } from "simplydo/react/ui/header";
import { Icon } from "simplydo/react/ui/icon";
import { Image } from "simplydo/react/ui/image";
import {
  Modal,
  ModalActions,
  ModalContent,
  ModalHeader,
} from "simplydo/react/ui/modal";

import api from "api";
import { useAppSelector } from "store";
import util from "utils/utils";

import IdeaBusinessProfileModal from "components/lib/IdeaBusinessProfileModal/IdeaBusinessProfileModal";
import type {
  IdeaBusinessProfile,
  IdeaBusinessProfileUpdateResponse,
} from "components/lib/IdeaBusinessProfileModal/types";

import {
  HomeRow,
  HomeRowActions,
  HomeRowContainer,
  HomeRowContent,
  HomeRowIcon,
} from "./HomeUI";

const SESSION_DISMISS_KEY = "businessProfileYearEndReviewDismissed";

type DashboardBusinessProfile = NonNullable<
  OpenAPI.GET<"/users/dashboard">["response"]["businessProfiles"]
>[number];

const wasDismissedThisSession = () => {
  try {
    return sessionStorage.getItem(SESSION_DISMISS_KEY) === "true";
  } catch {
    return false;
  }
};

const dismissForSession = () => {
  try {
    sessionStorage.setItem(SESSION_DISMISS_KEY, "true");
  } catch {
    // Ignore storage failures and still hide for this mount.
  }
};

const BusinessProfileYearEndReview = () => {
  const { t } = useTranslation();
  const user = useAppSelector((state) => state.user);
  const [dismissed, setDismissed] = useState(wasDismissedThisSession);
  const [editingProfile, setEditingProfile] =
    useState<DashboardBusinessProfile | null>(null);

  const enabled = util.organisationFeaturesEnabled(user, [
    "ideaBusinessProfiles",
  ]);
  const shouldLoad = enabled && !dismissed && !user?.ghostingAs;

  const dashboardQuery = useQuery(
    initUseQuery(api.users.dashboard, [], { isEnabled: shouldLoad }),
  );

  const profilesNeedingReview = useMemo(() => {
    if (!user) {
      return [];
    }
    return (dashboardQuery.data?.businessProfiles ?? []).filter(
      (profile) =>
        profile.needsReview &&
        util.hasPermission(
          user,
          "ideaBusinessProfile.editDetails",
          profile._id,
        ),
    );
  }, [dashboardQuery.data?.businessProfiles, user]);

  const onCancel = () => {
    dismissForSession();
    setDismissed(true);
  };

  const onProfileSaved = (profile: IdeaBusinessProfile) => {
    api.setQueryData(api.users.dashboard, [], (prev) => ({
      ...prev,
      businessProfiles: (prev?.businessProfiles ?? []).map((item) =>
        item._id === profile._id
          ? { ...item, ...profile, needsReview: false }
          : item,
      ),
    }));
    api.invalidateQueries(api.users.dashboard);
    api.invalidateQueries(api.users.getProfiles);
    setEditingProfile(null);
  };

  if (!shouldLoad || !profilesNeedingReview.length) {
    return null;
  }

  return (
    <>
      <Modal
        open={!editingProfile}
        onClose={onCancel}
        size="small"
        closeOnDimmerClick={false}
      >
        <ModalHeader>{t("home.businessProfiles.review.title")}</ModalHeader>
        <ModalContent>
          <p style={{ marginBottom: "16px" }}>
            {t("home.businessProfiles.review.description")}
          </p>
          <HomeRowContainer>
            {profilesNeedingReview.map((profile) => (
              <HomeRow key={profile._id}>
                <HomeRowIcon>
                  {profile.imageUrl ? (
                    <Image src={profile.imageUrl} />
                  ) : (
                    <Icon icon={BuildingIcon} size="large" />
                  )}
                </HomeRowIcon>
                <HomeRowContent>
                  <H4>{profile.name}</H4>
                </HomeRowContent>
                <HomeRowActions
                  style={{ flexDirection: "row", flexWrap: "wrap" }}
                >
                  <Button
                    size="small"
                    color="primary"
                    onClick={() => setEditingProfile(profile)}
                  >
                    {t("home.businessProfiles.review.update")}
                  </Button>
                </HomeRowActions>
              </HomeRow>
            ))}
          </HomeRowContainer>
        </ModalContent>
        <ModalActions>
          <Button variant="outline" color="grey" onClick={onCancel}>
            {t("generic.cancel")}
          </Button>
        </ModalActions>
      </Modal>
      {editingProfile ? (
        <IdeaBusinessProfileModal
          businessProfile={editingProfile}
          config={{ openInEditMode: true }}
          onClose={() => setEditingProfile(null)}
          onSave={onProfileSaved}
        />
      ) : null}
    </>
  );
};

export default BusinessProfileYearEndReview;
